import { Platform } from "react-native";

const KEY = "coravu.client.activeCaseId.v1";

/**
 * Active-case persistence. The client portal's sidebar switcher remembers
 * your last context across navigations via sessionStorage on web. On native
 * (no sessionStorage) the switcher falls back to "most-recent case" each
 * time. This is intentionally simple: no DB state needed, no cross-device
 * sync — the active case is a per-tab UX nicety, not a saved preference.
 */
export function readActiveCaseId(): string | null {
  if (Platform.OS !== "web") return null;
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage?.getItem(KEY) ?? null;
  } catch {
    return null;
  }
}

export function writeActiveCaseId(caseId: string | null): void {
  if (Platform.OS !== "web") return;
  try {
    if (typeof window === "undefined") return;
    if (caseId == null) window.sessionStorage?.removeItem(KEY);
    else window.sessionStorage?.setItem(KEY, caseId);
  } catch {
    // sessionStorage may be unavailable in private mode; fall through silently.
  }
}

/**
 * Pick a focal case from a list. Used as the default when no active case is
 * set yet. Picks open/judging cases first (sorted by soonest deadline), then
 * falls back to the most-recently-created case if everything's draft/closed.
 */
export function pickDefaultCaseId<
  T extends {
    id: string;
    status: string;
    closes_at: string | null;
    created_at?: string | null;
  },
>(rows: T[]): string | null {
  if (rows.length === 0) return null;
  const opens = rows.filter((r) => r.status === "open" || r.status === "judging");
  if (opens.length > 0) {
    const withDeadline = opens.filter((r) => r.closes_at);
    if (withDeadline.length > 0) {
      const sorted = [...withDeadline].sort(
        (a, b) =>
          new Date(a.closes_at as string).getTime() -
          new Date(b.closes_at as string).getTime()
      );
      return sorted[0].id;
    }
    return opens[0].id;
  }
  const sorted = [...rows].sort((a, b) => {
    const at = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
    return bt - at;
  });
  return sorted[0]?.id ?? null;
}

/**
 * Resolve the effective active case. Reads sessionStorage first; if that ID
 * isn't in `rows` (e.g. the case was deleted / archived) it falls back to
 * the default pick, then writes that back so the switcher stabilizes.
 */
export function resolveActiveCaseId<
  T extends { id: string; status: string; closes_at: string | null; created_at?: string | null },
>(rows: T[]): string | null {
  const stored = readActiveCaseId();
  if (stored && rows.some((r) => r.id === stored)) return stored;
  const fallback = pickDefaultCaseId(rows);
  if (fallback) writeActiveCaseId(fallback);
  return fallback;
}
